/**
 * The campaign taking shape, in the brand's own look (Brand OS colours, font, logo). Four chapters, each only the
 * must-haves; everything else is derived by the plan engine or asked later. Creators and their real posts are the
 * content, never AI mood images.
 */
import { useEffect, useState } from 'react';
import { Check, Loader2, Package, Plus, RefreshCw, Search, Sparkles, Users } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { NumberField } from '@/cowork/shared/NumberField';
import type { Theme } from '@/cowork/omniad/components/slides/SlideRenderer';
import type { ListCreator, OrderCreator, OrderIdea, OrderList, OrderPlan, OrderPrepare } from './types';
import { CHAPTERS, chapterReady, formatDate, formatNumber, productLabel, shortNumber, type Chapter, type OrderState } from './orderState';

interface Props {
  prep: OrderPrepare; theme: Theme; state: OrderState; chapter: Chapter; maxChapter: Chapter;
  set: (patch: Partial<OrderState>, wrote?: { field: string; value: string }) => void;
  pickProduct: (id: string) => void;
  plan: OrderPlan | null; planLoading: boolean;
  ideas: OrderIdea[] | null; ideasLoading: boolean; ideasError: string | null; onMoreIdeas: () => void;
  creators: OrderCreator[] | null; creatorsNote: string | null;
  lists: OrderList[] | null; listsNote: string | null; scouting: boolean; scoutNote: string | null;
  onToggleList: (l: OrderList) => void; onScout: () => void;
  onChapter: (ch: Chapter) => void; onOrder: () => void; ordering: boolean; orderError: string | null;
  result: { name: string; plan: OrderPlan } | null;
}

export function OrderCanvas(p: Props) {
  const { theme, state, chapter } = p;
  // The brand's own primary carries white text when it is dark enough; otherwise the deck theme's dark surface.
  const vars = {
    '--b-primary': theme.primary, '--b-dark': luminance(theme.primary) < 0.3 ? theme.primary : theme.bgDark, '--b-soft': theme.primarySoft, '--b-tint': theme.primaryTint, '--b-font': theme.fontDisplay,
  } as React.CSSProperties;
  return (
    <main id="order-canvas" className="min-w-0 flex-1 bg-[#F7F7F8] px-4 pb-20 pt-5 sm:px-8 lg:h-full lg:overflow-y-auto" style={vars}>
      <div className="mx-auto flex max-w-[980px] flex-col gap-6">
        {p.result ? <Done {...p} /> : (
          <>
            <nav aria-label="Chapters" className="flex flex-wrap gap-1.5">
              {CHAPTERS.map((c, i) => {
                const ch = i as Chapter; const reachable = ch <= p.maxChapter; const current = ch === chapter; const ok = chapterReady(state, ch) && ch < p.maxChapter;
                return (
                  <button key={c} type="button" disabled={!reachable} onClick={() => p.onChapter(ch)} aria-current={current ? 'step' : undefined}
                    className={cn('inline-flex items-center gap-2 rounded-full border bg-white py-1.5 pl-1.5 pr-3.5 text-[13px]', current ? 'border-[var(--b-primary)] text-[#101828]' : 'border-gray-200 text-[#667085]', !reachable && 'opacity-50')}
                    style={{ fontWeight: current ? 600 : 500 }}>
                    <span className={cn('grid h-5 w-5 place-items-center rounded-full text-[11px]', current ? 'bg-[var(--b-primary)] text-white' : ok ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-[#667085]')} style={{ fontWeight: 700 }}>
                      {ok && !current ? <Check className="h-3 w-3" aria-hidden /> : i + 1}
                    </span>{c}
                  </button>
                );
              })}
            </nav>
            {chapter === 0 && <BrandAndGoal {...p} />}
            {chapter === 1 && <AudienceAndIdea {...p} />}
            {chapter === 2 && <CreatorsAndPlan {...p} />}
            {chapter === 3 && <Order {...p} />}
          </>
        )}
      </div>
    </main>
  );
}

function luminance(hex: string): number {
  const h = hex.replace('#', ''); if (h.length < 6) return 0.5;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const H2 = ({ children, src }: { children: React.ReactNode; src?: string }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-2">
    <h2 className="text-[22px] leading-tight text-[#101828]" style={{ fontFamily: 'var(--b-font)', fontWeight: 700, letterSpacing: '-0.015em' }}>{children}</h2>
    {src && <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[#667085]"><span className="h-1.5 w-1.5 rounded-sm bg-[#BE43FF]" aria-hidden />{src}</span>}
  </div>
);
const tile = (on: boolean) => cn('flex min-h-[44px] flex-col gap-1 rounded-2xl border-[1.5px] bg-white p-3.5 text-left transition-colors', on ? 'border-[var(--b-primary)] bg-[var(--b-soft)] shadow-[inset_0_0_0_1px_var(--b-primary)]' : 'border-gray-200 hover:border-gray-300');
const Next = ({ ok, label, onClick, hint }: { ok: boolean; label: string; onClick: () => void; hint: string }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <span className="text-xs text-[#667085]">{ok ? '' : hint}</span>
    <button type="button" disabled={!ok} onClick={onClick} className="h-11 rounded-full bg-[var(--b-dark)] px-6 text-sm text-white disabled:opacity-40" style={{ fontWeight: 700 }}>{label}</button>
  </div>
);

/** A free-text answer, saved when the field is left or Enter is pressed (not on every key). */
function OwnWords({ id, label, value, placeholder, onCommit }: { id: string; label: string; value: string; placeholder: string; onCommit: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => { const v = draft.trim(); if (v !== value.trim()) onCommit(v); };
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5">
      <span className="text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>{label}</span>
      <input id={id} value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        className="h-11 rounded-xl border-[1.5px] border-gray-200 bg-white px-3 text-sm outline-none focus:border-[var(--b-primary)]" />
    </label>
  );
}

function Hero({ theme, prep, state, title, line }: Props & { title: string; line: string }) {
  const product = prep.products.find((x) => x.id === state.productId);
  return (
    <section className="relative flex flex-wrap items-center gap-6 overflow-hidden rounded-[22px] p-7 text-white" style={{ background: 'var(--b-dark)' }}>
      <div className="relative z-10 flex min-w-0 flex-[1_1_320px] flex-col gap-2.5">
        {theme.logoLightUrl ? <img src={theme.logoLightUrl} alt={prep.brand.name} className="h-7 w-auto max-w-[160px] self-start object-contain" /> : <span className="text-lg" style={{ fontFamily: 'var(--b-font)', fontWeight: 800 }}>{prep.brand.name}</span>}
        <h1 className="text-[clamp(26px,4vw,40px)] leading-[1.05]" style={{ fontFamily: 'var(--b-font)', fontWeight: 700, letterSpacing: '-0.02em' }}>{title}</h1>
        <p className="max-w-[52ch] text-white/75">{line}</p>
      </div>
      {product?.imageUrl
        ? <img src={product.imageUrl} alt={product.name} className="relative z-10 h-40 w-40 flex-none rounded-2xl bg-white object-contain p-2" />
        : <span className="relative z-10 grid h-40 w-40 flex-none place-items-center rounded-2xl bg-white/10" aria-hidden><Package className="h-12 w-12 text-white/60" /></span>}
      <span className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full opacity-50" style={{ background: 'radial-gradient(circle, var(--b-primary), transparent 70%)' }} aria-hidden />
    </section>
  );
}

function BrandAndGoal(p: Props) {
  const { prep, state, set } = p;
  const productName = productLabel(state, prep);
  return (
    <>
      <Hero {...p} title={`Your next creator campaign${productName ? ` for ${productName}` : ''}`} line="Built from your Brand OS: your products, your audiences, your look and what worked in your last campaigns." />
      {prep.products.length > 0 && (
        <section className="flex flex-col gap-3">
          <H2 src="Brand OS products">What are we promoting?</H2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2.5">
            {prep.products.slice(0, 9).map((pr) => (
              <button key={pr.id} type="button" aria-pressed={state.productId === pr.id} onClick={() => p.pickProduct(pr.id)} className={tile(state.productId === pr.id)}>
                <span className="text-sm text-[#101828]" style={{ fontWeight: 650 }}>{pr.name}</span>
                {pr.description && <span className="line-clamp-2 text-[12.5px] text-[#667085]">{pr.description}</span>}
              </button>
            ))}
          </div>
        </section>
      )}
      {prep.products.length === 0 && (
        <section className="flex flex-col gap-3">
          <H2>What are we promoting?</H2>
          <p className="text-sm text-[#667085]">Your Brand OS has no products yet. Name it here, or tell Lykke.</p>
          <OwnWords id="order-product" label="Product or service" value={state.productName} placeholder="For example: the new oat latte"
            onCommit={(v) => set({ productName: v, productId: null, idea: null }, v ? { field: 'Product', value: v } : undefined)} />
        </section>
      )}
      <section className="flex flex-col gap-3">
        <H2 src="Your media playbooks">What should it do?</H2>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2.5">
          {prep.goals.map((g) => (
            <button key={g.id} type="button" aria-pressed={state.goal === g.id} onClick={() => set({ goal: g.id }, { field: 'Goal', value: g.title })} className={tile(state.goal === g.id)}>
              <span className="text-sm text-[#101828]" style={{ fontWeight: 650 }}>{g.title}</span>
              <span className="text-[12.5px] text-[#667085]">{g.line}</span>
            </button>
          ))}
        </div>
      </section>
      <section className="flex flex-col gap-3">
        <H2 src={prep.evidence.history?.last ? `Prefilled from ${prep.evidence.history.last.name}` : undefined}>Where, when and how much</H2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>Market</span>
            <select value={state.market} onChange={(e) => set({ market: e.target.value }, { field: 'Market', value: prep.markets.find((m) => m.id === e.target.value)?.name || e.target.value })}
              className="h-11 rounded-xl border-[1.5px] border-gray-200 bg-white px-3 text-sm outline-none focus:border-[var(--b-primary)]">
              {prep.markets.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>Budget, {state.currency} excl. VAT</span>
            <NumberField min={1} group allowEmpty commit="blur" value={state.budget} onChange={(n) => set({ budget: n }, n ? { field: 'Budget', value: `${formatNumber(n)} ${state.currency}` } : undefined)}
              className="h-11 rounded-xl border-[1.5px] border-gray-200 bg-white px-3 text-sm outline-none focus:border-[var(--b-primary)]" />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>Start</span>
            <input type="date" value={state.start} min={prep.defaults.earliestStart} onChange={(e) => e.target.value >= prep.defaults.earliestStart && set({ start: e.target.value }, { field: 'Start', value: formatDate(e.target.value) })}
              className="h-11 rounded-xl border-[1.5px] border-gray-200 bg-white px-3 text-sm outline-none focus:border-[var(--b-primary)]" />
            <span className="text-xs text-[#667085]">Earliest four weeks from today, so creators can sign up.</span>
          </label>
        </div>
      </section>
      <Next ok={chapterReady(state, 0)} label="Show me the plan" onClick={() => p.onChapter(1)} hint="Pick a product and a goal to continue." />
    </>
  );
}

function AudienceAndIdea(p: Props) {
  const { prep, state, set } = p;
  const ev = prep.evidence;
  const cards: Array<{ src: string; body: React.ReactNode }> = [];
  if (ev.voice[0]) cards.push({ src: 'Customer voice', body: <q className="text-[16px] leading-snug" style={{ fontFamily: 'var(--b-font)' }}>{ev.voice[0].text}</q> });
  if (ev.market) cards.push({ src: `Market brief, ${prep.markets.find((m) => m.id === ev.market!.country)?.name || ev.market.country}`, body: <ul className="flex list-disc flex-col gap-1 pl-4 text-[13px]">{ev.market.trends.slice(0, 2).map((t) => <li key={t}>{t}</li>)}</ul> });
  if (ev.history?.last) cards.push({ src: 'Your campaigns', body: <p className="text-[13px]">{ev.history.campaigns} campaign{ev.history.campaigns === 1 ? '' : 's'} with Beatly. Last: {ev.history.last.name}{ev.history.last.impressions ? `, ${shortNumber(ev.history.last.impressions)} impressions` : ''}.</p> });
  if (ev.competitors.length) cards.push({ src: 'Competitors', body: <ul className="flex flex-col gap-1 text-[13px]">{ev.competitors.slice(0, 2).map((c) => <li key={c.name}><b>{c.name}</b>{c.angle ? `: ${c.angle}` : ''}</li>)}</ul> });
  return (
    <>
      {cards.length > 0 && (
        <section className="flex flex-col gap-3">
          <H2 src="Brand OS">Why this plan</H2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-2.5">
            {cards.map((c) => (
              <div key={c.src} className="flex flex-col gap-1.5 rounded-2xl border border-gray-200 bg-white p-3.5">
                <span className="text-[11.5px] text-[#667085]">{c.src}</span>{c.body}
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="flex flex-col gap-3">
        <H2 src="Brand OS audiences">Who should see it</H2>
        {prep.audiences.length === 0 && (
          <>
            <p className="text-sm text-[#667085]">Your Brand OS has no audiences yet. Describe who you want to reach, or tell Lykke.</p>
            <OwnWords id="order-audience" label="Who should see it" value={state.audienceText} placeholder="For example: coffee lovers in Stockholm, 25 to 40"
              onCommit={(v) => set({ audienceText: v }, v ? { field: 'Audience', value: v.length > 48 ? `${v.slice(0, 48)}…` : v } : undefined)} />
          </>
        )}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-3">
          {prep.audiences.map((a) => {
            const on = state.audienceIds.includes(a.id);
            return (
              <button key={a.id} type="button" aria-pressed={on}
                onClick={() => set({ audienceIds: on ? state.audienceIds.filter((x) => x !== a.id) : [...state.audienceIds, a.id] })}
                className={cn('flex flex-col overflow-hidden rounded-2xl border-[1.5px] bg-white text-left', on ? 'border-[var(--b-primary)] shadow-[inset_0_0_0_1px_var(--b-primary)]' : 'border-gray-200')}>
                {a.portraitUrl
                  ? <img src={a.portraitUrl} alt="" className="h-28 w-full object-cover" />
                  : <span className="grid h-28 w-full place-items-center" style={{ background: 'linear-gradient(160deg, var(--b-tint), var(--b-dark))' }} aria-hidden><Users className="h-9 w-9 text-white/70" /></span>}
                <span className="flex flex-col gap-1 p-3.5">
                  <span className="text-sm text-[#101828]" style={{ fontWeight: 650 }}>{a.name}{a.ageRange ? `, ${a.ageRange.replace(/[–-]/, ' to ')}` : ''}</span>
                  {a.description && <span className="line-clamp-2 text-[12.5px] text-[#667085]">{a.description}</span>}
                  <span className="text-xs text-[var(--b-primary)]" style={{ fontWeight: 600 }}>
                    {a.size ? `${shortNumber(a.size)} people on Meta` : ''}{a.size && state.productId && a.productIds.includes(state.productId) ? ' · ' : ''}{state.productId && a.productIds.includes(state.productId) ? 'linked to this product' : ''}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </section>
      <section className="flex flex-col gap-3">
        <H2 src="Grounded in your Brand OS">Pick the idea</H2>
        {p.ideasLoading && !p.ideas && (
          <div className="flex items-center gap-2 rounded-2xl border border-dashed border-gray-300 bg-white p-5 text-sm text-[#667085]"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />Lykke is writing three ideas from your Brand OS.</div>
        )}
        {p.ideasError && <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">{p.ideasError}</p>}
        <div className="flex flex-col gap-3">
          {(p.ideas || []).map((i) => {
            const on = state.idea?.title === i.title;
            return (
              <article key={i.id + i.title} className={cn('flex flex-col gap-2 rounded-[18px] border-[1.5px] bg-white p-4', on ? 'border-[var(--b-primary)] shadow-[inset_0_0_0_1px_var(--b-primary)]' : 'border-gray-200')}>
                <h3 className="text-xl text-[#101828]" style={{ fontFamily: 'var(--b-font)', fontWeight: 700, letterSpacing: '-0.015em' }}>{i.title}</h3>
                {i.hook && <p className="text-sm text-[var(--b-primary)]" style={{ fontWeight: 600 }}>&ldquo;{i.hook}&rdquo;</p>}
                <p className="text-[13px] text-[#475467]">{i.description}</p>
                {i.groundedIn.length > 0 && (
                  <div className="flex flex-col gap-0.5 border-t border-dashed border-gray-200 pt-2 text-xs text-[#667085]">
                    {i.groundedIn.map((g) => <span key={g.text}><b className="text-[#344054]">{g.source}:</b> {g.text}</span>)}
                  </div>
                )}
                <button type="button" aria-pressed={on} onClick={() => set({ idea: i }, { field: 'Idea', value: i.title })}
                  className={cn('mt-1 h-10 self-start rounded-full border-[1.5px] px-4 text-sm', on ? 'border-[var(--b-dark)] bg-[var(--b-dark)] text-white' : 'border-[var(--b-dark)] text-[var(--b-dark)]')} style={{ fontWeight: 700 }}>
                  {on ? 'Chosen' : 'Choose this idea'}
                </button>
              </article>
            );
          })}
        </div>
        {p.ideas && <button type="button" onClick={p.onMoreIdeas} disabled={p.ideasLoading} className="inline-flex items-center gap-1.5 self-start text-[13px] text-[#667085] underline underline-offset-4 disabled:opacity-50"><RefreshCw className={cn('h-3.5 w-3.5', p.ideasLoading && 'animate-spin')} aria-hidden />Show three other ideas</button>}
      </section>
      <Next ok={chapterReady(state, 1)} label="Show me the creators" onClick={() => p.onChapter(2)} hint="Pick an audience and an idea to continue." />
    </>
  );
}

function CreatorsAndPlan(p: Props) {
  const { prep, state, set, plan } = p;
  return (
    <>
      <section className="flex flex-col gap-3">
        <H2 src="Your campaigns on Beatly">Creators{state.idea ? ` for ${state.idea.title}` : ''}</H2>
        {!p.creators && <div className="flex items-center gap-2 text-sm text-[#667085]"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />Loading creators who worked with you.</div>}
        {p.creators && p.creators.length > 0 && (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
            {p.creators.map((c) => (
              <div key={c.handle} className="flex min-w-0 flex-col gap-2.5 rounded-2xl border border-gray-200 bg-white p-3">
                <div className="flex items-center gap-2.5">
                  <span className="grid h-10 w-10 flex-none place-items-center rounded-full text-xs text-white" style={{ background: 'var(--b-dark)', fontWeight: 700 }} aria-hidden>{(c.name || c.handle).replace('@', '').slice(0, 2).toUpperCase()}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-[#101828]" style={{ fontWeight: 600 }}>{c.handle}</div>
                    <div className="text-xs text-[#667085]"><span className="text-[var(--b-primary)]" style={{ fontWeight: 600 }}>Worked with you</span> {c.campaigns}×{c.impressions ? `, ${shortNumber(c.impressions)} impressions` : ''}</div>
                  </div>
                </div>
                {c.posts.length > 0 && (
                  <div className="grid grid-cols-3 gap-1.5">
                    {c.posts.map((post, i) => post.image && (
                      <a key={i} href={post.url} target="_blank" rel="noreferrer" className="block aspect-[9/16] overflow-hidden rounded-lg bg-gray-100">
                        <img src={post.image} alt={post.caption || `Post by ${c.handle}`} className="h-full w-full object-cover" loading="lazy" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-[#667085]">{p.creatorsNote || 'Creators who delivered for you before are asked first. After you order, you pick your creators from a shortlist.'}{plan ? ` We look for creators with ${plan.sizeBand} followers.` : ''}</p>
      </section>

      <section className="flex flex-col gap-3">
        <H2 src="Creator lists on Beatly">Pick creators like these</H2>
        <p className="-mt-1 text-[13px] text-[#667085]">Point at a list to show the feel you want. Optional: your shortlist is scouted for you either way.</p>
        {!p.lists && <div className="flex items-center gap-2 text-sm text-[#667085]"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />Finding creator lists that fit {prep.brand.name}.</div>}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-3">
          {(p.lists || []).map((l) => {
            const on = state.lists.some((x) => x.id === l.id);
            return (
              <div key={l.id} className={cn('flex min-w-0 flex-col overflow-hidden rounded-2xl border-[1.5px] bg-white', on ? 'border-[var(--b-primary)] shadow-[inset_0_0_0_1px_var(--b-primary)]' : 'border-gray-200')}>
                <div className="relative aspect-[16/9] w-full overflow-hidden" style={l.cover ? undefined : { background: 'linear-gradient(150deg, var(--b-tint), var(--b-dark))' }}>
                  {l.cover && <img src={l.cover} alt="" loading="lazy" className="h-full w-full object-cover" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" aria-hidden />
                  <div className="absolute inset-x-3 bottom-2.5 text-white">
                    <div className="line-clamp-2 text-[15px] leading-tight" style={{ fontFamily: 'var(--b-font)', fontWeight: 700 }}>{l.title}</div>
                    <div className="text-[11.5px] text-white/80">{l.count} creator{l.count === 1 ? '' : 's'}{l.scouted ? ' · Rex adds more after you order' : ''}</div>
                  </div>
                  {l.scouted && <span className="absolute left-3 top-3 rounded-full bg-white px-2 py-0.5 text-[10.5px] text-[var(--b-dark)]" style={{ fontWeight: 700 }}>New for this order</span>}
                  {on && <span className="absolute right-3 top-3 grid h-6 w-6 place-items-center rounded-full bg-white text-[var(--b-dark)]" aria-hidden><Check className="h-3.5 w-3.5" /></span>}
                </div>
                <div className="flex flex-1 flex-col gap-3 p-3.5">
                <ul className="flex flex-col gap-1.5">
                  {l.creators.slice(0, 4).map((c) => <CreatorRow key={c.id || c.handle} c={c} />)}
                </ul>
                {l.creators.length > 4 && (
                  <div className="flex -space-x-2" aria-label={`${l.creators.length - 4} more creators`}>
                    {l.creators.slice(4, 10).map((c) => <Avatar key={c.id || c.handle} c={c} size={26} ring />)}
                  </div>
                )}
                <button type="button" aria-pressed={on} onClick={() => p.onToggleList(l)}
                  className={cn('mt-auto h-9 self-start rounded-full border-[1.5px] px-3.5 text-[13px]', on ? 'border-[var(--b-dark)] bg-[var(--b-dark)] text-white' : 'border-[var(--b-dark)] text-[var(--b-dark)]')} style={{ fontWeight: 700 }}>
                  {on ? 'Using this list' : 'Use as reference'}
                </button>
                </div>
              </div>
            );
          })}
          <div className="flex min-w-0 flex-col justify-between gap-3 rounded-2xl border-[1.5px] border-dashed border-gray-300 bg-white/60 p-3.5">
            <div>
              <div className="flex items-center gap-2 text-sm text-[#101828]" style={{ fontWeight: 650 }}><Search className="h-4 w-4 text-[var(--b-primary)]" aria-hidden />Scout a new list for this campaign</div>
              <p className="mt-1 text-xs text-[#667085]">Lykke makes a list from your idea, market and creator size, fills it with a first search now, and Rex keeps scouting after you order.</p>
            </div>
            {p.scoutNote && <p className="text-xs text-amber-800">{p.scoutNote}</p>}
            <button type="button" onClick={p.onScout} disabled={p.scouting}
              className="inline-flex h-9 items-center gap-1.5 self-start rounded-full px-3.5 text-[13px] text-white disabled:opacity-50" style={{ background: 'var(--b-primary)', fontWeight: 700 }}>
              {p.scouting ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Plus className="h-3.5 w-3.5" aria-hidden />}{p.scouting ? 'Scouting' : 'Scout a list'}
            </button>
          </div>
        </div>
        {p.listsNote && <p className="text-xs text-[#667085]">{p.listsNote}</p>}
      </section>

      <section className="flex flex-col gap-3">
        <H2 src="Your media planner">What you get</H2>
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>Budget: {formatNumber(state.budget || 0)} {state.currency}</span>
          <input type="range" min={50000} max={2000000} step={25000} value={state.budget || 0} onChange={(e) => set({ budget: Number(e.target.value) })} className="w-full accent-[var(--b-dark)]" />
        </label>
        <div className={cn('grid grid-cols-2 gap-2.5 sm:grid-cols-4', p.planLoading && 'opacity-60')}>
          {[
            [plan ? String(plan.creators) : '…', 'creators'],
            [plan ? String(plan.pieces) : '…', 'pieces of content'],
            [plan ? `${shortNumber(plan.impressions.low)} to ${shortNumber(plan.impressions.high)}` : '…', 'estimated impressions'],
            [plan ? `${plan.weeks} weeks` : '…', `from ${formatDate(state.start)}`],
          ].map(([v, l]) => (
            <div key={l} className="flex min-w-0 flex-col rounded-2xl border border-gray-200 bg-white px-3.5 py-3">
              <b className="truncate text-[24px] tabular-nums text-[var(--b-dark)]" style={{ fontFamily: 'var(--b-font)', fontWeight: 700, letterSpacing: '-0.02em' }}>{v}</b>
              <span className="text-xs text-[#667085]">{l}</span>
            </div>
          ))}
        </div>
        {plan && (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            {plan.lines.map((l) => (
              <div key={l.title + l.platform} className="flex flex-wrap items-center gap-3 border-t border-gray-100 px-4 py-3 first:border-t-0">
                <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-[var(--b-soft)] text-[var(--b-primary)]" aria-hidden>{l.kind === 'ads' ? <Sparkles className="h-4 w-4" /> : <Users className="h-4 w-4" />}</span>
                <div className="min-w-0 flex-[1_1_220px]"><div className="text-sm text-[#101828]" style={{ fontWeight: 600 }}>{l.title}</div><div className="text-[12.5px] text-[#667085]">{l.detail}</div></div>
                {l.youApprove && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800" style={{ fontWeight: 700 }}>You approve creators and content</span>}
              </div>
            ))}
          </div>
        )}
        {plan && (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white p-4">
            <div className="grid min-w-[560px] items-center gap-x-1 gap-y-1.5 text-xs" style={{ gridTemplateColumns: `170px repeat(${plan.weeks}, minmax(36px, 1fr))` }} role="table" aria-label="Timeline by week">
              <span />{Array.from({ length: plan.weeks }, (_, w) => <span key={w} className="text-center text-[11px] text-[#98A2B3]">W{w + 1}</span>)}
              {plan.timeline.map((t) => (
                <TimelineRow key={t.what} weeks={plan.weeks} {...t} />
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <H2>How do creators get the product?</H2>
        <div className="flex flex-wrap gap-2">
          {prep.delivery.map((d) => (
            <button key={d.id} type="button" aria-pressed={state.delivery === d.id} onClick={() => set({ delivery: d.id }, { field: 'Product delivery', value: d.label })}
              className={cn('h-10 rounded-full border-[1.5px] px-4 text-sm', state.delivery === d.id ? 'border-[var(--b-dark)] bg-[var(--b-dark)] text-white' : 'border-gray-200 bg-white text-[#101828]')}>{d.label}</button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5"><span className="text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>Who approves creators and content?</span>
            <input value={state.approverName} onChange={(e) => set({ approverName: e.target.value })} className="h-11 rounded-xl border-[1.5px] border-gray-200 bg-white px-3 text-sm outline-none focus:border-[var(--b-primary)]" /></label>
          <label className="flex flex-col gap-1.5"><span className="text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>Their e-mail</span>
            <input type="email" value={state.approverEmail} onChange={(e) => set({ approverEmail: e.target.value })} className="h-11 rounded-xl border-[1.5px] border-gray-200 bg-white px-3 text-sm outline-none focus:border-[var(--b-primary)]" /></label>
        </div>
      </section>
      <Next ok={chapterReady(state, 2)} label="Continue to order" onClick={() => p.onChapter(3)} hint="Choose how creators get the product and who approves." />
    </>
  );
}

function Avatar({ c, size = 30, ring }: { c: ListCreator; size?: number; ring?: boolean }) {
  const initials = (c.name || c.handle).replace('@', '').slice(0, 2).toUpperCase();
  return c.avatar
    ? <img src={c.avatar} alt="" loading="lazy" className={cn('flex-none rounded-full bg-gray-100 object-cover', ring && 'ring-2 ring-white')} style={{ width: size, height: size }} />
    : <span className={cn('grid flex-none place-items-center rounded-full text-[10px] text-white', ring && 'ring-2 ring-white')} style={{ width: size, height: size, background: 'var(--b-dark)', fontWeight: 700 }} aria-hidden>{initials}</span>;
}

function CreatorRow({ c }: { c: ListCreator }) {
  return (
    <li className="flex min-w-0 items-center gap-2.5">
      <Avatar c={c} />
      <div className="min-w-0 flex-1">
        {c.url ? <a href={c.url} target="_blank" rel="noreferrer" className="block truncate text-[13px] text-[#101828] hover:underline" style={{ fontWeight: 600 }}>{c.handle}</a> : <span className="block truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{c.handle}</span>}
        <span className="text-[11.5px] text-[#667085]">{c.followers ? `${shortNumber(c.followers)} followers` : ''}{c.followers && c.platform ? ' · ' : ''}{c.platform ? c.platform.charAt(0) + c.platform.slice(1).toLowerCase() : ''}</span>
      </div>
    </li>
  );
}

function TimelineRow({ from, to, what, you, weeks }: { from: number; to: number; what: string; you: boolean; weeks: number }) {
  const a = Math.max(1, Math.min(from, weeks)); const b = Math.max(a, Math.min(to, weeks));
  return (
    <>
      <span className="truncate text-[#344054]">{what}</span>
      {a > 1 && <span style={{ gridColumn: `span ${a - 1}` }} />}
      <span className={cn('h-4 rounded', you ? 'border border-amber-500 bg-amber-100' : 'border border-[var(--b-primary)] bg-[var(--b-tint)]')} style={{ gridColumn: `span ${b - a + 1}` }} />
      {b < weeks && <span style={{ gridColumn: `span ${weeks - b}` }} />}
    </>
  );
}

function Order(p: Props) {
  const { prep, state, set, plan } = p;
  const productName = productLabel(state, prep);
  const goal = prep.goals.find((g) => g.id === state.goal);
  const offer = (id: 'full' | 'autonomous', title: string, line: string) => (
    <button type="button" aria-pressed={state.service === id} onClick={() => set({ service: id }, { field: 'Service', value: title })} className={cn(tile(state.service === id), 'gap-2 p-4')}>
      <span className="text-lg text-[#101828]" style={{ fontFamily: 'var(--b-font)', fontWeight: 700 }}>{title}</span>
      <span className="text-[13px] text-[#667085]">{line}</span>
    </button>
  );
  return (
    <>
      <section className="flex flex-col gap-3">
        <H2>How do you want to work with us?</H2>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {offer('full', 'Full service', 'A Beatly campaign manager leads your campaign with our agents and talks with you along the way.')}
          {offer('autonomous', 'Autonomous', 'Our agents run the steps in your plan. Lykke keeps you updated, and a Beatly specialist steps in when a person is needed.')}
        </div>
        <p className="text-xs text-[#667085]">Either way you decide on the creators and on each piece of content before it goes live.</p>
      </section>
      <section className="flex flex-col gap-3">
        <H2>Your order</H2>
        <dl className="rounded-2xl border border-gray-200 bg-white px-4">
          {[
            ['Campaign', [productName, state.idea?.title].filter(Boolean).join(', ')],
            ['Goal and market', `${goal?.title || 'Not set'}, ${prep.markets.find((m) => m.id === state.market)?.name || state.market}`],
            ['Starts', `${formatDate(state.start)}${plan ? `, ${plan.weeks} weeks` : ''}`],
            ['What you get', plan ? `${plan.creators} creators, ${plan.pieces} pieces of content` : ''],
            ['Campaign budget', `${formatNumber(state.budget || 0)} ${state.currency} excl. VAT`],
            ['Approver', state.approverEmail],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-t border-gray-100 py-2.5 text-[13.5px] first:border-t-0"><dt className="text-[#667085]">{k}</dt><dd className="text-right text-[#101828]" style={{ fontWeight: 600 }}>{v}</dd></div>
          ))}
        </dl>
        <label htmlFor="order-terms" className="flex items-start gap-2.5 text-[13.5px] text-[#344054]">
          <input id="order-terms" type="checkbox" checked={state.terms} onChange={(e) => set({ terms: e.target.checked })} className="mt-0.5 h-[18px] w-[18px] accent-[var(--b-dark)]" />
          <span>I accept the Beatly campaign terms for this order.</span>
        </label>
        {p.orderError && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{p.orderError}</p>}
        <button type="button" disabled={!chapterReady(state, 3) || p.ordering} onClick={p.onOrder}
          className="inline-flex h-[52px] items-center gap-2 self-start rounded-full px-7 text-base text-white disabled:opacity-40" style={{ background: 'var(--b-primary)', fontFamily: 'var(--b-font)', fontWeight: 700 }}>
          {p.ordering && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}Start my campaign
        </button>
      </section>
    </>
  );
}

function Done(p: Props) {
  const r = p.result!;
  return (
    <>
      <Hero {...p} title={`${p.state.idea?.title || 'Your campaign'} is on its way`} line={`${r.name}. Starts ${formatDate(p.state.start)}. ${p.state.service === 'autonomous' ? 'Autonomous' : 'Full service'}.`} />
      <section className="flex flex-col gap-3">
        <H2>What happens next</H2>
        <ul className="rounded-2xl border border-gray-200 bg-white">
          {[
            ['Now', 'Your campaign team checks the order and sets up the campaign', false],
            ...r.plan.timeline.map((t) => [t.from === t.to ? `Week ${t.from}` : `Weeks ${t.from} to ${t.to}`, t.what, t.you] as const),
          ].map(([w, t, you]) => (
            <li key={String(t)} className="flex flex-wrap items-center gap-3 border-t border-gray-100 px-4 py-3 first:border-t-0">
              <span className="w-24 flex-none text-xs text-[#667085]">{w}</span>
              <span className="min-w-0 flex-[1_1_220px] text-sm text-[#101828]" style={{ fontWeight: 600 }}>{t}</span>
              {you && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800" style={{ fontWeight: 700 }}>You decide</span>}
            </li>
          ))}
        </ul>
        {p.state.lists.some((l) => l.scouted) && <p className="text-sm text-[#344054]">Rex starts scouting for &ldquo;{p.state.lists.find((l) => l.scouted)!.title}&rdquo; when your campaign team confirms the order. New creators land in that list.</p>}
        <p className="text-sm text-[#667085]">Lykke keeps you posted here and in Messages.</p>
      </section>
    </>
  );
}
